import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface CustomerContextCustomer {
  id: string;
  type: 'account' | 'lead';
  name: string;
  email: string | null;
  city: string | null;
  /** Account staat op de blacklist (geen zaken meer doen). Leads altijd false. */
  blacklisted?: boolean;
  blacklistReden?: string | null;
}

export interface CustomerContextRecentCall {
  id: string;
  direction: string;
  status: string;
  started_at: string;
  brand: string;
  conversation_id: string | null;
  client_call_id: string | null;
}

export interface CustomerContextOfferte {
  id: string;
  name: string;
  status: string;
  totalInclBtw: number | null;
  createdDate: string | null;
}

export interface CustomerContextData {
  phone: string;
  customer: CustomerContextCustomer | null;
  recentCalls: CustomerContextRecentCall[];
  openOffertes: CustomerContextOfferte[];
  isLoading: boolean;
  error: string | null;
}

const cache = new Map<string, { at: number; data: CustomerContextData }>();
const TTL = 2 * 60 * 1000;

export function useCustomerContext(phone: string | null): CustomerContextData {
  const [data, setData] = useState<CustomerContextData>(() => ({
    phone: phone ?? '',
    customer: null,
    recentCalls: [],
    openOffertes: [],
    isLoading: false,
    error: null,
  }));

  useEffect(() => {
    if (!phone) {
      setData({ phone: '', customer: null, recentCalls: [], openOffertes: [], isLoading: false, error: null });
      return;
    }

    const cached = cache.get(phone);
    if (cached && Date.now() - cached.at < TTL) {
      setData(cached.data);
      return;
    }

    let cancelled = false;
    setData({ phone, customer: null, recentCalls: [], openOffertes: [], isLoading: true, error: null });

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;

        const r = await fetch(`/api/customers/phone-lookup?phone=${encodeURIComponent(phone)}`, { headers });
        if (!r.ok) throw new Error(`lookup failed: ${r.status}`);
        const json = await r.json();

        const next: CustomerContextData = {
          phone,
          customer: json.customer ?? null,
          recentCalls: json.recentCalls ?? [],
          openOffertes: json.openOffertes ?? [],
          isLoading: false,
          error: null,
        };
        cache.set(phone, { at: Date.now(), data: next });
        if (!cancelled) setData(next);
      } catch (err) {
        if (!cancelled) {
          setData({
            phone,
            customer: null,
            recentCalls: [],
            openOffertes: [],
            isLoading: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [phone]);

  return data;
}
